import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import './TextCompletionPlay.css';

const TextCompletionPlay = ({ question, handleAnswer, answerStatus }) => {
    const { t } = useTranslation('completion');
    const [answers, setAnswers] = useState({});
    const [selectedWord, setSelectedWord] = useState(null);
    const submitted = useRef(false);
    const gaps = Object.keys(question.correctAnswers || {});
    const words = question.words || [];
    const usedWords = Object.values(answers);
    const complete = gaps.length > 0 && gaps.every(gap => answers[gap] !== undefined);
    const locked = complete || answerStatus != null;

    const placeWord = (word, gapId) => {
        if (locked || submitted.current || !gaps.includes(gapId) || !words.includes(word)) return;
        setAnswers(previous => {
            if (previous[gapId] !== undefined || Object.values(previous).includes(word)) return previous;
            return { ...previous, [gapId]: word };
        });
        setSelectedWord(null);
    };

    const activateGap = gapId => {
        if (locked || submitted.current) return;
        if (answers[gapId] !== undefined) {
            setAnswers(previous => {
                const next = { ...previous };
                delete next[gapId];
                return next;
            });
            setSelectedWord(null);
        } else if (selectedWord !== null) {
            placeWord(selectedWord, gapId);
        }
    };

    useEffect(() => {
        if (!complete || submitted.current || answerStatus != null) return;
        submitted.current = true;
        // Play owns scoring and the transition to the next question/results.
        handleAnswer(Object.keys(question.correctAnswers).map(gap => answers[gap]).join(', '), question);
    }, [answers, complete, handleAnswer, question, answerStatus]);

    const renderGap = gapId => {
        const word = answers[gapId];
        const filled = word !== undefined;
        const resultClass = filled && answerStatus != null
            ? (word === question.correctAnswers[gapId] ? ' correct' : ' incorrect')
            : '';

        return (
            <button
                key={gapId}
                type="button"
                className={`completion-gap ${filled ? 'gap-text' : 'dropzone'}${resultClass}`}
                data-gap={gapId}
                disabled={locked}
                aria-label={filled
                    ? t('filledGap', { number: gaps.indexOf(gapId) + 1, word })
                    : t('emptyGap', { number: gaps.indexOf(gapId) + 1 })}
                onClick={() => activateGap(gapId)}
                onDragOver={event => {
                    if (!locked && !filled) event.preventDefault();
                }}
                onDrop={event => {
                    event.preventDefault();
                    placeWord(event.dataTransfer.getData('text/plain'), gapId);
                }}
            >
                {filled ? word : '\u00a0'}
            </button>
        );
    };

    // Keep the CMS text formatting, but render the gaps and words as React nodes.
    const text = parse(question.text || '', {
        replace(node) {
            if (node.type !== 'text' || !/\{\{[^{}]+\}\}/.test(node.data)) return;
            return <>{node.data.split(/(\{\{[^{}]+\}\})/g).map((part, index) => {
                const gapId = part.slice(2, -2);
                return <React.Fragment key={index}>
                    {part.startsWith('{{') && gaps.includes(gapId) ? renderGap(gapId) : part}
                </React.Fragment>;
            })}</>;
        },
    });

    return (
        <div className="text-completion-wrapper">
            <p className="completion-instructions">{t('instructions')}</p>
            <p className="completion-drag-hint">{t('dragHint')}</p>
            <div className="words-container flex flex-wrap gap-3 my-6 justify-center">
                {words.map((word, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`draggable-word btn btn-primary ${usedWords.includes(word) ? 'opacity-50' : ''}`}
                        disabled={locked || usedWords.includes(word)}
                        aria-pressed={selectedWord === word}
                        draggable={!locked && !usedWords.includes(word)}
                        onClick={() => setSelectedWord(previous => previous === word ? null : word)}
                        onDragStart={event => {
                            event.dataTransfer.setData('text/plain', word);
                            event.dataTransfer.effectAllowed = 'copy';
                            setSelectedWord(word);
                        }}
                        onDragEnd={() => setSelectedWord(null)}
                    >
                        {word}
                    </button>
                ))}
            </div>
            <p className="completion-selection" role="status">
                {selectedWord !== null ? t('selected', { word: selectedWord }) : '\u00a0'}
            </p>
            <div className="text-with-gaps">{text}</div>
        </div>
    );
};

export default TextCompletionPlay;
